// Landing page targeting "graduation gifts for him" across both moments:
// high school grad heading to campus (bridges /gifts-for-college-students)
// and college grad starting a first job (bridges /wallet-for-professionals).
import React from "react";
import Header from "../components/Header";
import Footer from "../components/Footer";
import SEO from "../components/SEO";
import {
  makeBreadcrumbJsonLd,
  makeFaqJsonLd,
} from "../seo/structuredData";
import heroPhoto from "../assets/newest-version/Full Front - Everything extended.jpg";

const futura = "'Futura', sans-serif";

const GRAD_FAQ = [
  {
    q: "Is The Brik a good graduation gift?",
    a: "Yes, because it marks the moment with something they'll use daily. The Brik is a metal wallet that holds an ID up front, 7-8 cards behind RFID protection, folded cash, and keys on an integrated ring, with a rechargeable tracking card built in. It's a $69.99 upgrade they carry every day after the ceremony.",
  },
  {
    q: "Is it better for a high school or college graduate?",
    a: "Both, for different reasons. A high school grad puts their new student ID in the front slot and their first dorm key on the ring. A college grad swaps in an office badge and starts their first job with their carry organized.",
  },
  {
    q: "Why give a practical gift instead of a sentimental one?",
    a: "Sentimental gifts live in a drawer. The Brik gets carried every day, and each time they badge into a dorm or an office, the gift is doing its job. Practical and meaningful aren't opposites when the gift lasts.",
  },
  {
    q: "Do I need to know anything about their phone?",
    a: "Just one thing: whether they use Apple or Android. Pick their platform at checkout and the Brik ships with the matching tracking card.",
  },
  {
    q: "When should I order for graduation season?",
    a: "Orders are processed in about 1 business day and arrive within 5-7 days. Ordering two weeks before the ceremony leaves comfortable margin, and there's a 30-day exchange guarantee.",
  },
];

const sectionHeading = {
  fontFamily: futura,
  fontSize: "clamp(26px, 3vw, 36px)",
  margin: "0 0 12px",
  letterSpacing: "-0.5px",
  color: "#111",
};

const bodyText = {
  fontFamily: futura,
  fontSize: "16px",
  lineHeight: 1.65,
  color: "#444",
  margin: "0 0 16px",
};

const ctaButton = {
  display: "inline-block",
  padding: "16px 32px",
  background: "#ff7d1a",
  color: "white",
  borderRadius: "10px",
  fontWeight: 700,
  fontSize: "15px",
  letterSpacing: "1px",
  fontFamily: futura,
  textTransform: "uppercase",
  textDecoration: "none",
  boxShadow: "0 6px 20px rgba(255,125,26,0.35)",
};

const GraduationGifts = () => {
  const faqJsonLd = makeFaqJsonLd(GRAD_FAQ);
  const breadcrumbJsonLd = makeBreadcrumbJsonLd([
    { name: "Home", path: "/" },
    { name: "Graduation Gifts", path: "/graduation-gifts" },
  ]);

  return (
    <div style={{ background: "white", minHeight: "100vh" }}>
      <SEO
        title="Graduation Gifts He'll Use Every Day"
        description="A graduation gift that doesn't end up in a drawer. The Brik holds their ID, cards, cash, and keys in one trackable metal wallet, ready for campus or a first job. $69.99."
        path="/graduation-gifts"
        keywords="graduation gifts, graduation gifts for him, college graduation gift, high school graduation gift for guys, practical graduation gift ideas, graduation gift for son"
        jsonLd={[faqJsonLd, breadcrumbJsonLd]}
      />
      <Header />

      {/* Hero */}
      <section
        style={{
          background: "radial-gradient(ellipse at top, #2a2a2a 0%, #0c0c0c 70%)",
          color: "white",
          padding: "70px 24px 80px",
        }}
      >
        <div style={{ maxWidth: "840px", margin: "0 auto", textAlign: "center" }}>
          <p
            style={{
              fontFamily: futura,
              color: "#ff7d1a",
              margin: "0 0 10px",
              fontSize: "13px",
              fontWeight: 700,
              letterSpacing: "2px",
            }}
          >
            GRADUATION GIFTS
          </p>
          <h1
            style={{
              fontFamily: futura,
              fontSize: "clamp(30px, 4.2vw, 52px)",
              lineHeight: 1.1,
              letterSpacing: "-1px",
              margin: "0 0 18px",
            }}
          >
            The Graduation Gift He'll Carry Every Day
          </h1>
          <p
            style={{
              fontFamily: futura,
              fontSize: "17px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              maxWidth: "660px",
              margin: "0 auto 26px",
            }}
          >
            A graduation gift shouldn't end up in a drawer with the cards and
            the tassel. The Brik is a metal wallet that holds their ID, cards,
            cash, and keys with a tracking card built in, ready for whatever
            comes after the ceremony. $69.99.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Get the Metal Brik · $69.99
          </a>
          <p
            style={{
              fontFamily: futura,
              fontSize: "13px",
              color: "#9be8b4",
              margin: "18px 0 0",
              fontWeight: 600,
            }}
          >
            In stock · arrives in 5-7 days · 30-day exchange guarantee
          </p>
          <div style={{ maxWidth: "560px", margin: "36px auto 0" }}>
            <img
              src={heroPhoto}
              alt="Graduation gift: The Brik metal wallet with ID, cards, cash, and keys"
              width={1024}
              height={1024}
              fetchPriority="high"
              style={{ width: "100%", height: "auto", borderRadius: "16px", display: "block" }}
            />
          </div>
        </div>
      </section>

      {/* Two moments */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "900px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "36px" }}>
            One gift, two graduations
          </h2>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(300px, 1fr))",
              gap: "24px",
            }}
          >
            <div
              style={{
                background: "#f7f7f7",
                borderRadius: "14px",
                padding: "28px 26px",
                border: "1px solid #e6e6e6",
              }}
            >
              <h3
                style={{
                  fontFamily: futura,
                  fontSize: "19px",
                  margin: "0 0 10px",
                  color: "#111",
                  letterSpacing: "-0.3px",
                }}
              >
                High school graduation: headed to campus
              </h3>
              <p style={{ ...bodyText, fontSize: "15px" }}>
                In two months they'll be swiping a student ID ten times a day
                and carrying a dorm key they cannot afford to lose. The Brik
                puts the ID in a quick-access front slot, the key on the
                integrated ring, and a tracker inside for the first time it
                gets left in a lecture hall.
              </p>
              <a
                href="/gifts-for-college-students"
                style={{
                  fontFamily: futura,
                  color: "#ff7d1a",
                  fontWeight: 700,
                  fontSize: "14px",
                  textDecoration: "none",
                }}
              >
                See the full college gift guide →
              </a>
            </div>
            <div
              style={{
                background: "#f7f7f7",
                borderRadius: "14px",
                padding: "28px 26px",
                border: "1px solid #e6e6e6",
              }}
            >
              <h3
                style={{
                  fontFamily: futura,
                  fontSize: "19px",
                  margin: "0 0 10px",
                  color: "#111",
                  letterSpacing: "-0.3px",
                }}
              >
                College graduation: starting the first job
              </h3>
              <p style={{ ...bodyText, fontSize: "15px" }}>
                The student ID slot becomes an office badge slot. The dorm key
                becomes an office fob. Machined black aluminum looks right on
                a conference table, and the RFID compartment covers the
                commute. It's a first-job carry in one piece.
              </p>
              <a
                href="/wallet-for-professionals"
                style={{
                  fontFamily: futura,
                  color: "#ff7d1a",
                  fontWeight: 700,
                  fontSize: "14px",
                  textDecoration: "none",
                }}
              >
                See the professional's setup →
              </a>
            </div>
          </div>
          <p
            style={{
              fontFamily: futura,
              fontSize: "14px",
              color: "#666",
              textAlign: "center",
              margin: "28px 0 0",
            }}
          >
            Gift shopping for a different occasion?{" "}
            <a href="/fathers-day-gifts" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              Father's Day
            </a>{" "}
            and{" "}
            <a href="/groomsmen-gifts" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              groomsmen packs
            </a>{" "}
            are covered too.
          </p>
        </div>
      </section>

      {/* Why practical wins */}
      <section style={{ background: "#f7f7f7", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={sectionHeading}>Practical is the sentiment</h2>
          <p style={bodyText}>
            Graduation gifts split into two piles: the sentimental ones that
            go in a memory box, and the practical ones that go in a pocket.
            The Brik lands in the second pile on purpose. Every time they
            badge through a door for the next few years, they're using the
            thing you gave them the day everything changed.
          </p>
          <p style={{ ...bodyText, margin: 0 }}>
            And because our founder built the first Brik as a student at Iowa
            State after a lost dorm key cost him $160, it's a gift with a
            story that fits the occasion:{" "}
            <a href="/about" style={{ color: "#ff7d1a", fontWeight: 600 }}>
              read where The Brik came from
            </a>
            .
          </p>
        </div>
      </section>

      {/* FAQ */}
      <section style={{ background: "white", padding: "70px 24px" }}>
        <div style={{ maxWidth: "720px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, textAlign: "center", marginBottom: "30px" }}>
            Graduation gift questions, answered
          </h2>
          {GRAD_FAQ.map((item) => (
            <details
              key={item.q}
              style={{
                background: "#f7f7f7",
                borderRadius: "12px",
                border: "1px solid #e6e6e6",
                padding: "18px 22px",
                marginBottom: "12px",
                fontFamily: futura,
              }}
            >
              <summary
                style={{
                  cursor: "pointer",
                  fontWeight: 700,
                  fontSize: "15px",
                  color: "#111",
                }}
              >
                {item.q}
              </summary>
              <p
                style={{
                  margin: "10px 0 0",
                  fontSize: "15px",
                  lineHeight: 1.55,
                  color: "#555",
                }}
              >
                {item.a}
              </p>
            </details>
          ))}
        </div>
      </section>

      {/* Final CTA */}
      <section
        style={{
          background: "#0c0c0c",
          color: "white",
          padding: "70px 24px",
          textAlign: "center",
        }}
      >
        <div style={{ maxWidth: "640px", margin: "0 auto" }}>
          <h2 style={{ ...sectionHeading, color: "white" }}>
            They're carrying more now. Literally.
          </h2>
          <p
            style={{
              fontFamily: futura,
              fontSize: "16px",
              lineHeight: 1.6,
              color: "#cfcfcf",
              margin: "0 0 26px",
            }}
          >
            $69.99, in stock, arrives in 5-7 days. Pick Apple or Android at
            checkout so the tracking card matches their phone.
          </p>
          <a href="/metal-wallet" style={ctaButton}>
            Buy the Metal Brik
          </a>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default GraduationGifts;
